/*
 * File: Story.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 13, 2024 at 9:21 AM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 11, 2025 at 8:48 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Utilities
import { buildDynamicMeta } from '../../../components/Meta';
import { formatTextRedactions, truncateString } from '../../../../utilities/utilities';
import { scrollPageToHash } from '../../../utilities/position';
import { toastError } from '../../../utilities/toaster';

// Services
import {
	fetchNextStory, fetchRandomStory, fetchRelatedStories, fetchStory
} from '../../../services/story';
import { recordView } from '../../../services/view';

// Store
import { updateCurrentStory, updateCurrentStoryError } from '../../../store/slices/currentStory/currentStory.slice';

// Components
import {
	Meta,
	AppNavigation,
	SchemaScript,
	StoryComponent,
	ErrorComponent,
	DetailPagePanel,
	LocaleLink,
	Button,
	Typography,
} from '../../../components';

// Constants
import { AVAILABLE_I18N_NAMESPACES, SOURCES } from '../../../../constants/base.constants';

// Styles
import * as S from './Story.styles';


/**
 * Component
 */

const Story = ({ meta: m }) => {

	// Get component parameters
	const { slug } = useParams();

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Create references
	const viewedStories = useRef([]);

	// Get current story from hook
	let { status: storyStatus, value: story } = useSelector((state) => state.currentStory);
	if (story?.link_component && story?.link_component !== slug) {
		storyStatus = 'initial';
		story = null;
	}

	// Check if loading
	const isLoading = useMemo(() => storyStatus !== 'final', [storyStatus]);

	// Create state handlers
	const [isFetching, setIsFetching] = useState(false);
	const [nextStory, setNextStory] = useState(null);
	const [previousStories, setPreviousStories] = useState([]);
	const [storyIndex, setStoryIndex] = useState(0);
	const [related, setRelated] = useState(null);

	// Use hooks
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();

	// Get location hash
	const { hash } = location;

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Build title object
	const title = useMemo(() => (story ? `${!story?.title ? `#${story?.number}` : story.title}` : ''), [story]);

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageStory.metaTitle', { partnerName: partner.name, storyTitle: title ? formatTextRedactions(title) : t('storyName') }),
		description: truncateString(formatTextRedactions(story?.story), 160),
		bodyClasses: 'hide-captcha',
		...m || {},
		...story && story.allow_indexing === false ? {
			shouldIndex: false
		} : undefined
	}), [m, story, partner, t, title]);

	// Define parent meta content
	const parentMeta = useMemo(() => ({
		title: t('pageHome.metaTitle', { partnerName: partner.name })
	}), [partner, t]);

	// Create partner domain
	const partnerDomain = useMemo(() => {
		let domain = `https://${partner.domain}`;
		if (partner.redirect_to_subdomain) {
			domain = `https://${partner.redirect_to_subdomain}.${partner.domain}`;
		}
		return domain;
	}, [partner]);

	// Move to previous story action
	const movePrevious = () => {

		// Update next index
		setStoryIndex(storyIndex - 1);

		// Update previous stories
		setPreviousStories(previousStories.slice(0, -1));
	};

	// Move to next story action
	const moveNext = (q) => {

		// Update next index
		setStoryIndex(storyIndex + 1);

		// Update previous stories
		setPreviousStories([...previousStories, q]);
	};

	// Handle fetch current story
	const fetchCurrentStory = async () => {
		try {

			// Update state
			setRelated(null);

			// Fetch story
			const { data: { result: { story: s } } } = await fetchStory({ slug });

			// Update viewed stories
			viewedStories.current.push(s.story_id);

			// Update story
			dispatch(updateCurrentStory(s));

		} catch (e) {

			// Update state
			dispatch(updateCurrentStoryError(e.message));
		}
	};

	// Handle fetch next story
	const fetchNext = async () => {
		try {

			// Fetch next story
			const { data: { result: { story: s } } } = await fetchNextStory({ storyId: story.story_id, viewedStories: viewedStories.current });

			// Update state
			setNextStory(s);

		} catch (e) {

			// Update state
			setNextStory(null);
		}
	};

	// Handle fetch random story
	const fetchRandom = async () => {

		// Update state
		setIsFetching(true);

		// Fetch random story
		try {

			// Fetch random story
			const { data: { result: { story: s } } } = await fetchRandomStory({ storyId: story.story_id, viewedStories: viewedStories.current });

			// Update state
			setIsFetching(false);

			// Update next index
			setStoryIndex(storyIndex + 1);

			// Update previous stories
			setPreviousStories([...previousStories, story]);

			// Navigate to new story
			navigate(`${locale.localePath}/story/${s.slug}`);

		} catch (e) {

			// Update state
			setIsFetching(false);

			// Show error
			toastError(uiMode, partner, t('pageStory.randomError'));
		}
	};

	// Handle fetch related stories
	const fetchRelated = async () => {
		try {

			// Fetch related stories
			const { data: { result: { stories } } } = await fetchRelatedStories({ storyId: story.story_id });

			// Update state
			setRelated(stories || []);

		} catch (e) {

			// Update state
			setRelated([]);
		}
	};

	// Handle actions on url update
	useEffect(() => {
		if (storyStatus === 'final') {

			// Scroll to hash
			scrollPageToHash(hash);
		}
	}, [storyStatus, hash]);

	// Handle actions on status update
	useEffect(() => {
		if (storyStatus === 'final') {

			// Update viewed
			viewedStories.current = [...new Set([...viewedStories.current, story.story_id])];

			// Record view
			recordView({
				source: SOURCES.STORY,
				entityId: story.story_id,
				urlPath: window.location.pathname
			});

			// Fetch related stories
			fetchRelated();

			// Fetch next story
			fetchNext();
		}
	}, [storyStatus]);

	// Handle actions on component load
	useEffect(() => {

		// Check if should load
		if (storyStatus !== 'final') {

			// Fetch current story
			fetchCurrentStory();
		}

	}, [slug]);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Schema.org */}
			<SchemaScript schema={{
				'@context': 'http://schema.org',
				'@type': 'BreadcrumbList',
				'@id': `${partnerDomain}/story/${story?.link_component}/#BreadcrumbList`,
				itemListElement: [
					{
						'@type': 'ListItem',
						position: 1,
						item: {
							'@id': `${partnerDomain}/feed/#ListItem`,
							name: buildDynamicMeta(parentMeta.title, { partner }),
							url: `${partnerDomain}${locale.localePath}`
						}
					},
					{
						'@type': 'ListItem',
						position: 2,
						item: {
							'@id': `${partnerDomain}/story/${story?.link_component}/#ListItem`,
							name: buildDynamicMeta(meta.title, { partner }),
							url: `${partnerDomain}${locale.localePath}/story/${story?.link_component}`
						}
					}
				]
			}}
			/>
			<SchemaScript schema={{
				'@context': 'https://schema.org',
				'@type': 'ItemPage',
				'@id': `${partnerDomain}/story/${story?.link_component}/#ItemPage`,
				name: formatTextRedactions(title),
				description: formatTextRedactions(story?.story),
				url: `${partnerDomain}${locale.localePath}/story/${story?.link_component}`,
				inLanguage: locale.localeBaseId,
				isPartOf: {
					'@id': `${partnerDomain}/#WebSite`
				},
				publisher: {
					'@id': `${partner.website}/#Organization`
				},
				breadcrumb: {
					'@id': `${partnerDomain}/story/${story?.link_component}/#BreadcrumbList`
				},
				mainEntity: {
					'@type': ['CreativeWork', 'ShortStory'],
					'@id': `${partnerDomain}/story/${story?.link_component}/#CreativeWork`,
					name: formatTextRedactions(title),
					text: formatTextRedactions(story?.story),
					url: `${partnerDomain}${locale.localePath}/story/${story?.link_component}`,
					isAccessibleForFree: true
				}
			}}
			/>

			{/* Component Content */}
			<AppNavigation showFooter>
				<S.Wrapper>

					{/* Story Card */}
					{storyStatus !== 'error' ? (
						<>
							<S.StoryCard>

								{/* Story Component */}
								<StoryComponent
									story={story}
									isLoading={isLoading}
									showBackButton
									navigationBar={(
										<S.NavigationBar>

											{/* Previous Button */}
											{previousStories.length > storyIndex - 1 && previousStories.length > 0 ? (
												<LocaleLink to={`/story/${previousStories[storyIndex - 1].link_component}`} onClick={movePrevious}>
													<Button disabled={isFetching} variation="secondary">
														<FontAwesomeIcon icon={['fas', 'arrow-left-long']} />
														<Typography tag="span" weight="semibold">
															<Trans t={t} i18nKey="pageStory.previousStory">
																Previous Story
															</Trans>
														</Typography>
													</Button>
												</LocaleLink>
											) : <div />}

											{/* Random Button */}
											{story && (
												<Button disabled={isFetching} isLoading={isFetching} onClick={fetchRandom} variation="secondary">
													<Typography tag="span" weight="semibold">
														<Trans t={t} i18nKey="pageStory.randomStory">
															Random Story
														</Trans>
													</Typography>
													<FontAwesomeIcon icon={['fas', 'shuffle']} />
												</Button>
											)}

											{/* Next Button */}
											{nextStory ? (
												<LocaleLink to={`/story/${nextStory.slug}`} onClick={() => { moveNext(story); }}>
													<Button disabled={isFetching} variation="secondary">
														<Typography tag="span" weight="semibold">
															<Trans t={t} i18nKey="pageStory.nextStory">
																Next Story
															</Trans>
														</Typography>
														<FontAwesomeIcon icon={['fas', 'arrow-right-long']} />
													</Button>
												</LocaleLink>
											) : <div />}

										</S.NavigationBar>
									)}
								/>

							</S.StoryCard>

							{/* Detail Page Panel */}
							<DetailPagePanel
								source={SOURCES.STORY}
								sourceId={story?.story_id}
								related={related}
								onRelatedClick={() => { moveNext(story); }}
								className="detailPanel"
							/>
						</>
					) : <ErrorComponent showMeta />}
				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

Story.propTypes = {
	meta: PropTypes.shape()
};
Story.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default Story;
